import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InjectedFormProps } from 'redux-form';

import { linkMyWaitroseCard, resetMyWaitroseRequestError } from 'redux/modules/mywaitrose/actions';
import { setContactAddress } from 'redux/modules/address/actions';
import {
  addressesLoading,
  getActiveAddresses,
  getContactAddress,
} from 'redux/modules/address/selectors';
import { getMyWaitroseRequestError } from 'redux/modules/mywaitrose/selectors';
import { Address } from 'redux/modules/address/state/initial-state';

import Card from '@johnlewispartnership/wtr-ingredients/ingredients/Card';
import Alert from '@johnlewispartnership/wtr-ingredients/ingredients/Alert';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';

import Skeleton from 'components/Skeleton';
import PageTitle from 'components/PageTitle';
import Form, { FormData } from 'components/MyWaitrose/Form';
import { ChooseAddress, addressValidator } from 'components/Address';

import styles from './LinkMyWaitroseCard.scss';

export interface LinkMyWaitroseCardProps
  extends Pick<InjectedFormProps<FormData>, 'handleSubmit' | 'submitting'> {
  // submitted doesn't appear to be a prop that redux-form supplies, nor is it passed down from the
  // parent component. At run time, it is undefined. It should be removed?
  submitted: boolean;
}

const LinkMyWaitroseCard = ({ handleSubmit, submitted, submitting }: LinkMyWaitroseCardProps) => {
  const dispatch = useDispatch<WtrDispatch>();
  const isLoading = useSelector(addressesLoading);
  const serverError = useSelector(getMyWaitroseRequestError);
  const addresses = useSelector(getActiveAddresses);
  const contactAddress = useSelector(getContactAddress);

  const errorAlertRegex = /^4[0-9][0-9]/;
  const showErrorAlert = errorAlertRegex.test((serverError?.status ?? 200).toString());

  return isLoading ? (
    <Skeleton height="100vh" withSpinner />
  ) : (
    <section className={styles.mainSection} data-testid="link-my-waitrose-card">
      <div className="container-fluid">
        <div className="col-xs-12of12 col-lg-6of12 col-lg-push-3of12">
          <header>
            {/* @ts-expect-error untyped component */}
            <PageTitle text="Link My Waitrose Card" display underline />
          </header>
          {showErrorAlert && (
            <div className={styles.alert}>
              <Alert
                type="error"
                title={serverError?.response?.body?.message}
                message="If you require further support please call Waitrose Customer Service on freephone 0800 188 884 between the hours of 8am-10pm Monday - Friday, 8am-9pm Saturday and 9am-7pm Sunday"
                data-testid="error-alert"
              />
            </div>
          )}
          <Card>
            <Typography styleAs="paragraphHeading" textStyle="heading-l">
              Add your card number to your account to benefit from online membership offers.
            </Typography>
            <Typography styleAs="paragraph" textStyle="body-m">
              Your myWaitrose account must be registered with a contact address. This will be used
              for all Waitrose communications you choose to receive
            </Typography>
            <Form
              serverError={serverError}
              onSubmitAction={data => {
                return dispatch(linkMyWaitroseCard(data));
              }}
              handleSubmit={handleSubmit}
              onResetValidation={() => dispatch(resetMyWaitroseRequestError())}
              submitted={submitted}
              submitting={submitting}
              linkCard
            >
              <ChooseAddress
                addresses={addresses}
                initialChosenAddress={contactAddress}
                modalTitle="Select a contact address"
                addAddressTitle="Add a contact address"
                buttonMessage={contactAddress ? 'Change contact address' : 'Add contact address'}
                manualOnly={false}
                // @ts-expect-error untyped component
                onAfterSelect={(address: Address) => dispatch(setContactAddress(address))}
                validate={addressValidator}
              />
            </Form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default LinkMyWaitroseCard;
